import React, { useEffect, useRef, useState } from "react";
import Button from "@mui/material/Button";
import { enterFullscreen, fullscreenRequired, isFullscreen } from "../../utils/fullscreen";

// Full screen is mandatory on desktops and laptops. Out of full screen the questions are covered
// and the test timer is paused (setIsFullscreenExited -> Timer.jsx); OK goes back into full screen.
// Every exit is counted (examDetail[0].fullscreenExitCount, saved with the answers). The test is
// never aborted for it. Phones and tablets: not required, exits are only counted.
const FullscreenHandler = ({ examDetail, setIsFullscreenExited }) => {
  const required = useRef(fullscreenRequired()).current;
  const [blocked, setBlocked] = useState(() => required && !isFullscreen());
  const [refused, setRefused] = useState(false);
  const wasFullscreen = useRef(isFullscreen());

  useEffect(() => {
    setIsFullscreenExited?.(blocked);
  }, [blocked, setIsFullscreenExited]);

  useEffect(() => {
    const onChange = () => {
      const now = isFullscreen();
      if (wasFullscreen.current && !now && examDetail?.[0]) {
        examDetail[0].fullscreenExitCount = (examDetail[0].fullscreenExitCount || 0) + 1;
      }
      wasFullscreen.current = now;
      if (required) setBlocked(!now);
      if (now) setRefused(false);
    };

    document.addEventListener("fullscreenchange", onChange);
    document.addEventListener("webkitfullscreenchange", onChange);

    return () => {
      document.removeEventListener("fullscreenchange", onChange);
      document.removeEventListener("webkitfullscreenchange", onChange);
      setIsFullscreenExited?.(false);
    };
  }, [examDetail, required, setIsFullscreenExited]);

  if (!blocked) return null;

  const backToFullscreen = async () => {
    const ok = await enterFullscreen();
    if (!ok) setRefused(true);
  };

  return (
    <div
      role="dialog"
      aria-modal="true"
      style={{
        position: "fixed",
        inset: 0,
        zIndex: 2000,
        background: "rgba(15, 23, 42, 0.96)",
        display: "flex",
        alignItems: "center",
        justifyContent: "center",
        padding: "16px",
      }}
    >
      <div style={{ background: "#fff", borderRadius: "12px", padding: "28px 24px", maxWidth: "440px", textAlign: "center" }}>
        <h2 style={{ margin: "0 0 12px", fontSize: "20px" }}>This test runs in full screen only</h2>
        <p style={{ margin: "0 0 8px", color: "#334155" }}>
          Your test is paused and the timer has stopped. Click OK to return to full screen and continue.
        </p>
        {examDetail?.[0]?.fullscreenExitCount ? (
          <p style={{ margin: "0 0 16px", color: "#64748b", fontSize: "13px" }}>
            Leaving full screen is recorded ({examDetail[0].fullscreenExitCount} so far).
          </p>
        ) : null}
        {refused && (
          <p style={{ margin: "0 0 16px", color: "#b91c1c", fontSize: "13px" }}>
            Your browser didn't switch to full screen. Click OK again, or press F11 (Windows) / Ctrl + Cmd + F (Mac).
          </p>
        )}
        <Button variant="contained" color="primary" onClick={backToFullscreen} autoFocus>
          OK
        </Button>
      </div>
    </div>
  );
};

export default FullscreenHandler;
